import React, { useState } from "react";
import { CheckCircle2, Sparkles, MessageCircle } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { PageBanner } from "../components/Shared";
import { useContent } from "../context/ContentContext";
import { sendInquiry } from "../lib/api";
import { usePageMeta } from "../hooks/usePageMeta";

const CustomPackage = () => {
  const { t, lang } = useLang();
  usePageMeta({ title: "Customize Your Hajj or Umrah Package", description: "Build a personalised Hajj or Umrah package — choose hotels, dates, duration and budget. Fly Jannah sends your quote on WhatsApp within 24 hours." });
  const { IMAGES, CONTACT } = useContent();
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", type: "Umrah", travelers: "2",
    depart: "", duration: "12", hotel: "5 Star", city: "Dhaka", budget: "", message: "",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const waNumber = (CONTACT.whatsapp || "").replace(/\D/g, "") || (CONTACT.phone2 || "").replace(/\D/g, "").replace(/^0/, "880");
  const waMessage = () => {
    const f = t.custom.form;
    const rows = [
      ["", `*${lang === "en" ? "New Customize Package Request" : "নতুন কাস্টমাইজ প্যাকেজ অনুরোধ"}* — Fly Jannah`],
      [f.name, form.name], [f.phone, form.phone], [f.email, form.email], [f.type, form.type],
      [f.travelers, form.travelers], [f.depart, form.depart], [f.duration, form.duration], [f.hotel, form.hotel],
      [f.city, form.city], [f.budget, form.budget], [f.message, form.message],
    ].filter(([, v]) => v);
    return rows.map(([k, v]) => (k ? `*${k}:* ${v}` : v)).join("\n");
  };
  const waLink = () => `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage())}`;

  const submit = (e) => {
    e.preventDefault();
    sendInquiry("custom", form).catch(() => {});
    window.open(waLink(), "_blank", "noopener");
    setSubmitted(true);
    window.scrollTo({ top: 300, behavior: "smooth" });
  };

  const f = t.custom.form;
  const inputCls =
    "w-full px-4 py-3 rounded-lg border border-jannah-sand bg-white text-jannah-green-dark focus:outline-none focus:ring-2 focus:ring-jannah-green/40";
  const labelCls = "block text-sm font-medium text-jannah-green-dark mb-1.5";

  return (
    <div>
      <PageBanner title={t.custom.title} subtitle={t.custom.sub} image={IMAGES.pageBanner} />

      <section className="section-pad bg-jannah-cream">
        <div className="max-w-4xl mx-auto px-6">
          {submitted ? (
            <div className="bg-white rounded-2xl border border-jannah-sand p-12 text-center shadow-lg">
              <CheckCircle2 className="mx-auto text-jannah-green mb-5" size={64} />
              <h2 className="text-3xl font-bold text-jannah-green-dark mb-3">
                {lang === "en" ? "Request Received!" : "অনুরোধ গৃহীত হয়েছে!"}
              </h2>
              <p className="text-muted-foreground max-w-lg mx-auto">{t.custom.success}</p>
              <a href={waLink()} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#25D366] text-white font-semibold hover:bg-[#1ebe5d]" data-testid="custom-whatsapp-again">
                <MessageCircle size={18} /> {lang === "en" ? "Open WhatsApp again" : "আবার হোয়াটসঅ্যাপ খুলুন"}
              </a>
              <br />
              <button
                onClick={() => setSubmitted(false)}
                className="mt-8 px-7 py-3 rounded-full bg-jannah-green text-white font-semibold hover:bg-jannah-green-dark"
              >
                {lang === "en" ? "Submit Another" : "আরেকটি জমা দিন"}
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-jannah-sand p-8 md:p-10 shadow-lg">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-11 h-11 rounded-full bg-jannah-gold/20 flex items-center justify-center">
                  <Sparkles className="text-jannah-gold" size={22} />
                </div>
                <p className="text-muted-foreground text-sm">{t.custom.sub}</p>
              </div>
              <form onSubmit={submit} className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>{f.name}</label>
                  <input required value={form.name} onChange={set("name")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{f.email}</label>
                  <input type="email" required value={form.email} onChange={set("email")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{f.phone}</label>
                  <input required value={form.phone} onChange={set("phone")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{f.type}</label>
                  <select value={form.type} onChange={set("type")} className={inputCls}>
                    <option>Umrah</option>
                    <option>Hajj</option>
                    <option>Halal Tour</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{f.travelers}</label>
                  <input type="number" min="1" value={form.travelers} onChange={set("travelers")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{f.depart}</label>
                  <input type="date" value={form.depart} onChange={set("depart")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{f.duration}</label>
                  <input type="number" min="3" value={form.duration} onChange={set("duration")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{f.hotel}</label>
                  <select value={form.hotel} onChange={set("hotel")} className={inputCls}>
                    <option>5 Star</option>
                    <option>4 Star</option>
                    <option>3 Star</option>
                    <option>Economy</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{f.city}</label>
                  <input value={form.city} onChange={set("city")} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>{f.budget}</label>
                  <input type="number" value={form.budget} onChange={set("budget")} className={inputCls} />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>{f.message}</label>
                  <textarea rows="4" value={form.message} onChange={set("message")} className={inputCls} />
                </div>
                <div className="md:col-span-2">
                  <button
                    type="submit"
                    data-testid="custom-submit-btn"
                    className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full btn-gold font-semibold"
                  >
                    {lang === "en" ? "Send Request on WhatsApp" : "হোয়াটসঅ্যাপে অনুরোধ পাঠান"} <MessageCircle size={17} />
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CustomPackage;
